import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@apollo/client';
import { GET_CAR, UPDATE_DISTANCE_LIMIT } from '../../services/carService/queries';
import { IDistanceLimit } from '../../../__generated__/graphql';
import { Button } from '../button';
import { useBrandLocale } from '../../hooks/useBrandLocale';
import { useCarDistanceUnit } from '../../hooks/useCarDistanceUnit';
import {
  convertLimit,
  dailyDistanceChoices,
  defaultDailyDistance,
  defaultMonthlyDistance,
} from '../../utils/units';

const Container = styled.div`
  ${tw`p-6 max-w-md`}
`;

const Header = styled.div`
  ${tw`flex items-center gap-2 mb-6`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold`}
`;

const InfoIcon = styled(FontAwesomeIcon)`
  ${tw`text-text-tertiary cursor-help`}
`;

const Section = styled.div`
  ${tw`mb-6`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-4`}
`;

const LearnMore = styled.a`
  ${tw`text-black cursor-pointer mb-6 inline-block`}
  &:hover {
    ${tw`text-black`}
  }
`;

const ToggleContainer = styled.div`
  ${tw`flex items-center justify-between mb-6`}
`;

const ToggleLabel = styled.span`
  ${tw`font-medium`}
`;

const Toggle = styled.label`
  ${tw`relative inline-flex items-center cursor-pointer`}
`;

const ToggleInput = styled.input`
  ${tw`sr-only`}
`;

const ToggleSlider = styled.div<{ checked: boolean }>`
  ${tw`w-11 h-6 bg-bg-secondary rounded-full`}
  ${({ checked }) => checked && tw`bg-black`}

  &::after {
    content: '';
    ${tw`absolute h-4 w-4 left-1 top-1 bg-white rounded-full transition-transform`}
    ${({ checked }) => checked && tw`transform translate-x-full`}
  }
`;

const InputGroup = styled.div`
  ${tw`mb-6`}
`;

const InputLabel = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-1`}
`;

const Select = styled.select`
  ${tw`w-full p-2 border rounded-md appearance-none bg-white`}
`;

const NumberInput = styled.input`
  ${tw`w-full p-2 border rounded-md`}
`;

const TipBox = styled.div`
  ${tw`bg-bg-secondary p-4 rounded-lg flex items-start gap-3 mb-6`}
`;

const SaveButtonContainer = styled.div`
  ${tw`w-full py-2`}
`;

const TabBar = styled.div`
  ${tw`flex bg-bg-secondary rounded-lg p-1 mb-6`}
`;

const TabButton = styled.button<{ active: boolean }>`
  ${tw`flex-1 py-2 rounded-md font-medium transition-colors`}
  ${({ active }) => (active ? tw`bg-white text-black shadow-card` : tw`text-text-secondary`)}
`;

type RentalMode = 'daily' | 'monthly';

interface DistanceLimitsProps {
  carId: string;
  distanceLimit: IDistanceLimit | null;
}

const DistanceLimits: React.FC<DistanceLimitsProps> = ({ carId, distanceLimit }) => {
  // Spec 028: the form is in this site's unit; a limit stored in another unit is shown converted.
  const { distanceUnit: unit } = useBrandLocale();
  const { unit: storedUnit, loaded: storedUnitLoaded } = useCarDistanceUnit(carId);
  const km = unit === 'km';
  const [mode, setMode] = useState<RentalMode>('daily');
  const [isUnlimited, setIsUnlimited] = useState(distanceLimit?.unlimitedMileage ?? false);
  const [dailyMiles, setDailyMiles] = useState(
    distanceLimit?.dailyDistanceLimit ?? defaultDailyDistance(unit),
  );
  const [isMonthlyUnlimited, setIsMonthlyUnlimited] = useState(
    distanceLimit?.monthlyUnlimitedMileage ?? false,
  );
  const [monthlyMiles, setMonthlyMiles] = useState(
    distanceLimit?.monthlyDistanceLimit ?? defaultMonthlyDistance(unit),
  );
  useEffect(() => {
    if (!storedUnitLoaded) return;
    const daily = distanceLimit?.dailyDistanceLimit;
    const monthly = distanceLimit?.monthlyDistanceLimit;
    setDailyMiles(
      daily != null ? convertLimit(daily, storedUnit, unit) : defaultDailyDistance(unit),
    );
    setMonthlyMiles(
      monthly != null ? convertLimit(monthly, storedUnit, unit) : defaultMonthlyDistance(unit),
    );
  }, [distanceLimit, storedUnit, storedUnitLoaded, unit]);
  const savedInOtherUnit =
    storedUnitLoaded && storedUnit !== unit && distanceLimit?.dailyDistanceLimit != null;
  const dailyChoices = Array.from(new Set([...dailyDistanceChoices(unit), dailyMiles])).sort(
    (a, b) => a - b,
  );

  const [updateDistanceLimits, { loading }] = useMutation(UPDATE_DISTANCE_LIMIT, {
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
    onCompleted: () => {
      toast.success('Distance limits updated successfully');
    },
    onError: () => {
      toast.error('Error updating distance limits');
    },
  });

  const handleSave = async () => {
    await updateDistanceLimits({
      variables: {
        carId,
        distanceLimit: {
          dailyDistanceLimit: dailyMiles,
          unlimitedMileage: isUnlimited,
          monthlyDistanceLimit: monthlyMiles,
          monthlyUnlimitedMileage: isMonthlyUnlimited,
          unit,
        },
      },
    });
  };

  return (
    <Container>
      <Header>
        <Title>Distance limits</Title>
        <InfoIcon icon={faInfoCircle} />
      </Header>

      <TabBar role="tablist">
        <TabButton role="tab" active={mode === 'daily'} onClick={() => setMode('daily')}>
          Daily
        </TabButton>
        <TabButton role="tab" active={mode === 'monthly'} onClick={() => setMode('monthly')}>
          Monthly
        </TabButton>
      </TabBar>

      {mode === 'daily' ? (
        <Section>
          <Description>
            Guests who book shorter trips tend to drive more per day. Attract the bookings and
            receive a special badge by offering unlimited.
          </Description>
          <LearnMore>Learn more</LearnMore>

          <ToggleContainer>
            <ToggleLabel>Unlimited daily distance</ToggleLabel>
            <Toggle>
              <ToggleInput
                type="checkbox"
                checked={isUnlimited}
                onChange={e => setIsUnlimited(e.target.checked)}
              />
              <ToggleSlider checked={isUnlimited} />
            </Toggle>
          </ToggleContainer>

          {!isUnlimited && (
            <InputGroup>
              <InputLabel>{km ? 'Kilometres per day' : 'Miles per day'}</InputLabel>
              <Select value={dailyMiles} onChange={e => setDailyMiles(Number(e.target.value))}>
                {dailyChoices.map(choice => (
                  <option key={choice} value={choice}>
                    {choice}
                  </option>
                ))}
              </Select>
              {savedInOtherUnit && (
                <Description>
                  Saved in {storedUnit === 'km' ? 'km' : 'miles'} (
                  {distanceLimit?.dailyDistanceLimit} {storedUnit === 'km' ? 'km' : 'mi'}/day).
                  Saving here stores it in {km ? 'km' : 'miles'}.
                </Description>
              )}
            </InputGroup>
          )}

          <TipBox>
            <FontAwesomeIcon icon={faInfoCircle} tw="text-black mt-1" />
            <p>Turn on unlimited daily distance to attract more bookings.</p>
          </TipBox>
        </Section>
      ) : (
        <Section>
          <Description>
            Monthly-rental guests typically drive longer total distances. Pick a per-day allowance
            (it&apos;s multiplied by the trip duration) or unlimited distance.
          </Description>

          <ToggleContainer>
            <ToggleLabel>Unlimited monthly distance</ToggleLabel>
            <Toggle>
              <ToggleInput
                type="checkbox"
                checked={isMonthlyUnlimited}
                onChange={e => setIsMonthlyUnlimited(e.target.checked)}
              />
              <ToggleSlider checked={isMonthlyUnlimited} />
            </Toggle>
          </ToggleContainer>

          {!isMonthlyUnlimited && (
            <InputGroup>
              <InputLabel>
                {km ? 'Kilometres per month (30-day month)' : 'Miles per month (30-day month)'}
              </InputLabel>
              <NumberInput
                type="number"
                min={1}
                value={monthlyMiles}
                onChange={e => setMonthlyMiles(Number(e.target.value))}
              />
            </InputGroup>
          )}

          <TipBox>
            <FontAwesomeIcon icon={faInfoCircle} tw="text-black mt-1" />
            <p>
              {km ? 'Monthly distance' : 'Monthly mileage'} applies only to bookings classified
              MONTHLY (30+ days with a monthly price set). The allowance is prorated: a 45-day trip
              gets{' '}
              <strong>
                {monthlyMiles} × 45 / 30 = {Math.round((monthlyMiles * 45) / 30)}{' '}
                {km ? 'km' : 'miles'}
              </strong>
              .
            </p>
          </TipBox>
        </Section>
      )}

      <SaveButtonContainer>
        <Button text="Save" onClick={handleSave} isDisabled={loading} isLoading={loading} />
      </SaveButtonContainer>
    </Container>
  );
};

export default DistanceLimits;
